/**
 * StandupGenerateButton Component
 * AIDLC Administrative Backend
 *
 * Button to auto-generate standup entries from agent activity data.
 */

import { useState } from 'react';
import { Button } from '../../shared/Button';
import { generateStandups } from '../../../services/standupService';
import type { StandupEntry } from '../../../types/projects';

interface StandupGenerateButtonProps {
  projectId: string;
  onGenerated: (entries: StandupEntry[]) => void;
}

export function StandupGenerateButton({ projectId, onGenerated }: StandupGenerateButtonProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const entries = await generateStandups(projectId);
      onGenerated(entries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate standups');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="secondary"
        size="sm"
        onClick={handleGenerate}
        isLoading={isGenerating}
      >
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Generate Standup
      </Button>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}
